import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { CimaCore, Modal, Scroll, Util, WindowReference, XmStore, XmStoreUtil } from 'core/services';
import { CmsActivity, CmsMyAccountModals } from 'store/cms/models';
import { HeroBannerInfo } from 'store/hero-banner/models';
import { BannerAction, PROMO_ROLLCAP_BANNERIDS, PROMO_ROLLCAP_BANNERIDS_ADDED, PROMO_ROLLCAP_BANNERIDS_PENDING } from 'my-account/constants';
import { BannerService } from 'my-account/services/banner';
import { StoreAction } from 'store/actions';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';
@Component({
    selector: 'hero-banner',
    styleUrls: ['./banner.scss'],
    templateUrl: './banner.html',
    standalone: false
})
export class HeroBannerComponent implements OnInit, OnDestroy {
    @Input() public isMemberViewer: boolean;
    @Input() public isPrimary: boolean;
    @Output() public addonsScrollAction: EventEmitter<string> = new EventEmitter<string>();

    public currentBanner: CmsActivityBannerTemplate;
    public heading: string;
    public description: string;
    public body: string;
    public imageOptions: MediaImageOptions;
    public buttonText: string;
    public buttonAttributes: ButtonAttributes;
    public isLoading: boolean = true;
    public isExtendedUserRolesResi: boolean = false;
    public paymentGatewayUrl: string;
    public promoRollCapModalData: PromoRollCapModal;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private bannerInfo: HeroBannerInfo;
    private pageData: CmsActivity;
    private scroll: Scroll;
    private window: WindowReference;
    private bannerService: BannerService;
    private config: IXMOptions;
    private cimaCore: CimaCore;
    private modal: Modal;

    constructor(xmStore: XmStore, scroll: Scroll, window: WindowReference, bannerService: BannerService, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore, modal: Modal) {
        Object.assign(this, { xmStore, scroll, window, bannerService, config, cimaCore, modal });
    }

    public ngOnInit(): void {
        this.isExtendedUserRolesResi = this.config.ENABLE_EXTENDED_USER_ROLES && !this.cimaCore.isBusinessUser;
        this.paymentGatewayUrl = this.config.PAYMENT_BASE_URL;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsActivity>(CmsActivity), (pageData: CmsActivity) => {
            this.pageData = pageData;
        }));

        if (this.isMemberViewer) {
            this.isLoading = false;
            this.updatePageData();
        } else {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<HeroBannerInfo>(StoreAction.GET_HERO_BANNER_INFO, HeroBannerInfo), (bannerInfo: HeroBannerInfo) => {
                this.bannerInfo = bannerInfo;
                this.isLoading = false;
                this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccountModals>(CmsMyAccountModals), (cms: CmsMyAccountModals) => {
                    this.promoRollCapModalData = PROMO_ROLLCAP_BANNERIDS_PENDING.includes(this.bannerInfo.id) ? cms.modals.promoRollCapPending : cms.modals.promoRollCapAdded;
                }));
                this.updatePageData();
            }, () => {
                this.isLoading = false;
            }));
        }

        
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    // in case of button, set action here
    public goToCtaAction(): void {
        switch (this.currentBanner.ctaButton.action) {
            case BannerAction.SCROLL:
                this.scroll.toHash('my-data');
                break;
            case BannerAction.RELOAD:
                this.window.reload();
                break;
            case BannerAction.SCROLL_TO_GTP:
            case BannerAction.SCROLL_TO_CANMEX:
                this.addonsScrollAction.emit(this.currentBanner?.ctaButton?.action);
                this.scroll.toHash('addons-section');
                break;
            case BannerAction.OPEN_MODAL:
                if (PROMO_ROLLCAP_BANNERIDS.includes(this.bannerInfo.id)) {
                    this.modal.alert({
                        close: true,
                        header: this.promoRollCapModalData.header,
                        content: this.replaceModalContent(this.promoRollCapModalData.content)
                    });
                    break;
                } else {
                    break;
                }
        }
    }

    public get isNoCta(): boolean {
        const btn: ButtonAttributes = this.buttonAttributes;

        return !btn || (!btn.hasInternalLink && !btn.hasExternalLink && !btn.hasButton);
    }

    private replaceModalContent(content: string[]): string[] {
        const amountValue: string = PROMO_ROLLCAP_BANNERIDS_ADDED.includes(this.bannerInfo.id)
            ? this.bannerInfo.ctaButton.currentPromoAmount
            : this.bannerInfo.ctaButton.newPromoAmount;

        return content.map((text: string) => text
            .replace('[EXPIRY_DATE]', this.bannerInfo.ctaButton.currentPromoExpiryDt || '')
            .replace('[DURATION]', this.bannerInfo.ctaButton.newPromoTenure || '')
            .replace('[START_DATE]', this.bannerInfo.ctaButton.currentPromoStartDate || '')
            .replace('[END_DATE]', this.bannerInfo.ctaButton.currentPromoEndDate || '')
            .replace('[AMOUNT]', amountValue || ''));
    }


    private updatePageData(): void {
        if (this.isMemberViewer) {
            this.currentBanner = this.pageData.getDefaultMemberViewerBanner;
            this.heading = this.currentBanner.header || '';
            this.description = this.currentBanner.subheader || '';
            this.body = this.currentBanner.description || '';
        } else {
            this.currentBanner = this.pageData.getCurrentHeroBanner(this.bannerInfo);
            this.heading = this.pageData.getHeroBannerHeading(this.currentBanner, this.bannerInfo);
            this.description = this.pageData.getHeroBannerDescription(this.currentBanner, this.bannerInfo);
            this.body = this.pageData.getHeroBannerBody(this.currentBanner, this.bannerInfo);
            this.buttonText = this.currentBanner.ctaButton ? this.currentBanner.ctaButton.label : '';
            if (this.currentBanner.ctaButton) {
                this.buttonAttributes = this.bannerService.setBannerButtonAction(this.currentBanner, this.bannerInfo, this.isExtendedUserRolesResi, this.isMemberViewer, this.isPrimary, this.paymentGatewayUrl);
            }
        }

        this.imageOptions = {
            mobile: {
                base: this.pageData.getBannerImageUrl(this.currentBanner),
                alt: this.currentBanner.image.alt,
                useBoundingBox: true,
                trimTransparency: true
            }
        };
    }
}
